import { Component } from '@theme/component';
import { loadGsap } from '@theme/gsap-loader';

/**
 * @typedef {Object} ImageSequenceRefs
 * @property {HTMLCanvasElement} canvas
 * @property {HTMLElement} [title]
 * @property {HTMLElement} [richText]
 */

const FILES_BASE = 'https://cdn.shopify.com/s/files/1/0954/7707/2210/files';
const MOBILE_QUERY = '(max-width: 749px)';

/** @type {Map<string, HTMLImageElement[]>} */
const frameCache = new Map();

let extraFadeDone = false;

/**
 * Scroll-driven image sequence. Behaviour matches the live EU version
 * (mobile frame sets, scroll length, scrub smoothing, end hold, visibility
 * toggles, mobile offset) while loading GSAP only when mounted.
 *
 * @extends {Component<ImageSequenceRefs>}
 */
class ImageSequenceComponent extends Component {
  requiredRefs = ['canvas'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {object[]} */
  #tweens = [];

  #mediaQuery = window.matchMedia(MOBILE_QUERY);

  #lastWidth = window.innerWidth;

  #gsap = null;

  #render = null;

  #resizeCanvas = null;

  #measureLayout = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.#mediaQuery.addEventListener('change', this.#onModeChange);
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mediaQuery.removeEventListener('change', this.#onModeChange);
    this.#abortController.abort();
    this.#killTweens();
  }

  #onModeChange = () => {
    this.#killTweens();
    this.#build();
  };

  #onViewportChange = () => {
    this.#resizeCanvas?.();
    this.#measureLayout?.();
    this.#render?.();

    const width = window.innerWidth;
    if (width === this.#lastWidth) return;

    this.#lastWidth = width;
    window.ScrollTrigger?.refresh();
  };

  #killTweens() {
    this.#tweens.forEach((tween) => {
      tween.scrollTrigger?.kill();
      tween.kill();
    });
    this.#tweens = [];

    if (this.#gsap) {
      [this.refs.title, this.refs.richText].forEach((el) => {
        if (el) this.#gsap.set(el, { clearProps: 'opacity' });
      });
    }
  }

  async #init() {
    const { gsapUrl, scrollTriggerUrl } = this.dataset;
    if (!gsapUrl || !scrollTriggerUrl) return;

    const gsap = await loadGsap(gsapUrl, scrollTriggerUrl);
    if (!this.isConnected) return;

    this.#gsap = gsap;
    this.#build();

    window.addEventListener('resize', this.#onViewportChange, { signal: this.#abortController.signal });
    window.addEventListener('load', () => window.ScrollTrigger?.refresh(), {
      signal: this.#abortController.signal,
    });

    // Same hardcoded section fade as live EU on the Proshot page.
    if (!extraFadeDone) {
      const extraFadeId = '#shopify-section-template--30053462212946__section_xkmfNz';
      if (document.querySelector(extraFadeId)) {
        extraFadeDone = true;
        gsap.to(extraFadeId, {
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: extraFadeId,
            start: '60% top',
            end: '85% top',
            scrub: true,
          },
        });
      }
    }
  }

  #build() {
    const gsap = this.#gsap;
    const { canvas, title, richText } = this.refs;
    const context = canvas.getContext('2d');
    if (!context) return;

    const isMobile = this.#mediaQuery.matches;
    const allowed = isMobile ? this.dataset.showMobile !== 'false' : this.dataset.showDesktop !== 'false';
    if (!allowed) return;

    const mobileFolder = (this.dataset.mobileFolder || '').trim();
    const useMobileSet = isMobile && mobileFolder !== '';
    const folder = useMobileSet ? mobileFolder : this.dataset.folder || 'PROSHOTSTACK';
    const frameCount = Math.max(
      1,
      Number(useMobileSet ? this.dataset.mobileFrameCount : this.dataset.frameCount) || 1
    );
    const mobileOffsetY = Number(this.dataset.mobileOffsetY || 0);
    const paddingTop = Number(this.dataset.paddingTop || 0);
    const scrubSetting = Number(this.dataset.scrub || 0);
    const scrub = scrubSetting > 0 ? scrubSetting : true;
    const endHold = Number(this.dataset.endHold || 0);
    const holdFraction = endHold > 0 ? Math.min(endHold, 0.5) : 0;

    const images = loadFrames(folder, frameCount);
    const sequence = { frame: 0 };

    let titleTopInCanvas = 0;
    let headerInset = 0;

    this.#resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    this.#measureLayout = () => {
      const canvasRect = canvas.getBoundingClientRect();
      titleTopInCanvas = title ? title.getBoundingClientRect().top - canvasRect.top : canvas.height;

      const header = document.getElementById('header-component');
      headerInset = header ? header.getBoundingClientRect().height : 0;
    };

    const render = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);

      const img = images[Math.round(sequence.frame)];
      if (!img || !img.complete || !img.naturalWidth) return;

      const srcW = img.naturalWidth;
      const srcH = img.naturalHeight;
      const dstW = canvas.width;
      const dstH = canvas.height;

      const mobileScale = parseFloat(this.dataset.mobileScale || '');
      const desktopScale = parseFloat(this.dataset.desktopScale || '');
      const scaleMultiplier = isMobile
        ? !isNaN(mobileScale)
          ? mobileScale
          : 0.6
        : !isNaN(desktopScale)
          ? desktopScale
          : 1;

      const coverScale = Math.max(dstW / srcW, dstH / srcH) * scaleMultiplier;
      let scale = coverScale;
      let x = (dstW - srcW * scale) / 2;
      let y = (dstH - srcH * scale) / 2;

      if (isMobile) {
        const topLimit = headerInset;
        const gap = Math.max(paddingTop, 0);
        const bottomLimit = title ? titleTopInCanvas - gap : dstH;
        const availableH = Math.max(1, bottomLimit - topLimit);
        scale = Math.min(coverScale, availableH / srcH);

        const drawW = srcW * scale;
        const drawH = srcH * scale;
        x = (dstW - drawW) / 2;
        y = topLimit + (availableH - drawH) / 2 + mobileOffsetY;
      }

      context.drawImage(img, x, y, srcW * scale, srcH * scale);
    };

    this.#render = render;

    this.#resizeCanvas();
    this.#measureLayout();

    if (images[0].complete && images[0].naturalWidth) {
      render();
    } else {
      images[0].addEventListener(
        'load',
        () => {
          requestAnimationFrame(() => {
            this.#resizeCanvas?.();
            this.#measureLayout?.();
            render();
          });
        },
        { once: true }
      );
    }

    const sticky = this.querySelector('.sequence-sticky');
    const travel = () => {
      const stickyH = sticky ? sticky.offsetHeight : window.innerHeight;
      return Math.max(1, Math.round((this.offsetHeight - stickyH) * (1 - holdFraction)));
    };

    this.#tweens.push(
      gsap.to(sequence, {
        frame: frameCount - 1,
        snap: 'frame',
        ease: 'none',
        scrollTrigger: {
          trigger: this,
          start: 'top top',
          end: () => `+=${travel()}`,
          scrub,
          invalidateOnRefresh: true,
        },
        onUpdate: render,
      })
    );

    [title, richText].forEach((el) => {
      if (!el) return;

      // Two scrubbed tweens on the same CSS property would fight over the
      // recorded start value, so both drive a proxy and the opacity is
      // computed from them. Reverses correctly when scrolling back up.
      const fade = { enter: 0, exit: 0 };
      const applyOpacity = () => {
        el.style.opacity = (fade.enter * (1 - fade.exit)).toFixed(3);
      };
      applyOpacity();

      this.#tweens.push(
        gsap.to(fade, {
          enter: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: this,
            start: 'top 15%',
            end: 'top -10%',
            scrub: true,
            invalidateOnRefresh: true,
          },
          onUpdate: applyOpacity,
        })
      );

      this.#tweens.push(
        gsap.to(fade, {
          exit: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: this,
            start: '60% top',
            end: '85% top',
            scrub: true,
            invalidateOnRefresh: true,
          },
          onUpdate: applyOpacity,
        })
      );
    });

    requestAnimationFrame(() => {
      this.#resizeCanvas?.();
      this.#measureLayout?.();
      render();
      window.ScrollTrigger?.refresh();
    });
  }
}

/**
 * @param {string} folder
 * @param {number} frameCount
 * @returns {HTMLImageElement[]}
 */
function loadFrames(folder, frameCount) {
  const key = `${folder}|${frameCount}`;
  if (frameCache.has(key)) return frameCache.get(key);

  const images = [];
  for (let i = 0; i < frameCount; i++) {
    const img = new Image();
    img.src = `${FILES_BASE}/${folder}_${i.toString().padStart(4, '0')}.png`;
    images.push(img);
  }

  frameCache.set(key, images);
  return images;
}

if (!customElements.get('image-sequence')) {
  customElements.define('image-sequence', ImageSequenceComponent);
}
